import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { login } from '../store/slices/authSlice';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
      const body: any = { username, password };
      if (mfaToken) body.mfa_token = mfaToken;

      const response = await fetch(`${apiUrl}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.detail === 'MFA token required') {
          setMfaRequired(true);
          setError('This account has MFA enabled. Enter your 6-digit code.');
          return;
        }
        throw new Error(data.detail || 'Login failed');
      }

      localStorage.setItem('token', data.access_token);
      localStorage.setItem('username', username);

      if (data.mfa_setup_required) {
        // Provisional token — MFA not configured yet, must set it up before continuing.
        navigate('/setup-mfa', { replace: true });
        return;
      }

      dispatch(login({ token: data.access_token, username }));
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Card sx={{ width: '100%', mt: 3 }}>
          <CardContent>
            <Typography component="h1" variant="h4" align="center" gutterBottom>
              Trading Assistant
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
              Sign in to access your trading dashboard
            </Typography>

            {error && (
              <Alert severity={mfaRequired ? 'info' : 'error'} sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus={!mfaRequired}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading || mfaRequired}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Password"
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading || mfaRequired}
              />

              {mfaRequired && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  autoFocus
                  label="MFA Token (6 digits)"
                  name="mfaToken"
                  value={mfaToken}
                  onChange={(e) => setMfaToken(e.target.value)}
                  disabled={loading}
                  inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                  helperText="Enter the 6-digit code from your authenticator app"
                />
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : mfaRequired ? 'Verify & Sign In' : 'Sign In'}
              </Button>

              {mfaRequired && (
                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  onClick={() => { setMfaRequired(false); setMfaToken(''); setError(''); }}
                >
                  Back
                </Button>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Don't have an account?{' '}
                  <Link href="/signup" underline="hover">
                    Create account
                  </Link>
                </Typography>
              </Box>

              {!mfaRequired && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Already set up MFA?{' '}
                    <Link
                      href="#"
                      underline="hover"
                      onClick={(e) => {
                        e.preventDefault();
                        const t = localStorage.getItem('token');
                        const base = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                        window.open(t ? `${base}/mfa-setup?token=${t}` : `${base}/mfa-setup`, '_blank');
                      }}
                    >
                      Open MFA setup
                    </Link>
                    {' '}(log in first)
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;
